import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { KpiCard, ReportHeader, ReportSkeleton, CARD } from '../components/intelligence/ReportKit';
import { CampaignWizard } from '../components/intelligence/CampaignWizard';
import { CampaignAnalytics } from '../components/intelligence/CampaignAnalytics';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { Send, Plus, Wallet, Loader2, Pause, Play, Zap, Trash2, Mail, MessageSquare, Phone, ChevronDown, BarChart3, RefreshCw, X, ShieldCheck, Receipt } from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { getIntelAuth } from '../utils/intelPerms';
import { toast } from 'sonner';

const CH_ICON = { email: Mail, sms: MessageSquare, whatsapp: Phone };
const STATUS_STYLE = {
  active: 'bg-[#EBF2FF] text-[#2563EB] ring-blue-100',
  paused: 'bg-slate-100 text-slate-500 ring-slate-200',
  draft: 'bg-slate-100 text-slate-500 ring-slate-200',
  completed: 'bg-blue-50 text-blue-700 ring-blue-100',
};

const AMOUNT_PRESETS = { usd: [1000, 2500, 5000], aed: [5000, 10000, 20000] };
const THRESHOLD_PRESETS = { usd: [500, 1000, 2500], aed: [2000, 5000, 10000] };

const Toggle = ({ on, onChange, disabled, testid }) => (
  <button type="button" onClick={() => !disabled && onChange(!on)} data-testid={testid} aria-pressed={on}
    className={`relative w-10 h-6 rounded-full transition-colors duration-150 shrink-0 ${on ? 'bg-[#2563EB]' : 'bg-slate-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-150 ${on ? 'translate-x-4' : ''}`} />
  </button>
);

const TopupModal = ({ open, onClose, orgId, wallet, onDone }) => {
  const { formatMoney } = useOperatorLocale();
  const currency = wallet?.currency || 'usd';
  const presets = AMOUNT_PRESETS[currency] || AMOUNT_PRESETS.usd;
  const thresholds = THRESHOLD_PRESETS[currency] || THRESHOLD_PRESETS.usd;
  const [amount, setAmount] = useState(presets[1]);
  const [busy, setBusy] = useState(false);
  const [ar, setAr] = useState({ enabled: false, thresholdCents: thresholds[0], amountCents: presets[1] });
  const [savingAr, setSavingAr] = useState(false);

  useEffect(() => {
    if (open && wallet?.autoRecharge) {
      setAr({
        enabled: wallet.autoRecharge.enabled,
        thresholdCents: wallet.autoRecharge.thresholdCents || thresholds[0],
        amountCents: wallet.autoRecharge.amountCents || presets[1],
      });
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;
  const saved = wallet?.savedCard;
  const fmt = (c) => formatMoney(c / 100);
  const fees = wallet?.fees || { platformPct: 0.03, stripePct: 0.029, stripeFixedCents: 30 };
  const bfFee = Math.ceil(amount * fees.platformPct);
  const stripeFee = Math.ceil(amount * fees.stripePct) + fees.stripeFixedCents;
  const total = amount + bfFee + stripeFee;

  const goCheckout = async () => {
    setBusy(true);
    try {
      const res = await apiV2.post(`/organizations/${orgId}/marketing/wallet/topup`, { amount_cents: amount });
      window.location.href = res.data.checkoutUrl;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setBusy(false);
    }
  };
  const goRecharge = async () => {
    setBusy(true);
    try {
      const res = await apiV2.post(`/organizations/${orgId}/marketing/wallet/recharge`, { amount_cents: amount });
      toast.success(`Recharged — balance ${fmt(res.data.balanceCents)}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recharge failed');
    } finally {
      setBusy(false);
    }
  };
  const saveAutoRecharge = async (next) => {
    setSavingAr(true);
    try {
      const res = await apiV2.put(`/organizations/${orgId}/marketing/wallet/settings`, {
        auto_recharge_enabled: next.enabled,
        threshold_cents: next.thresholdCents,
        amount_cents: next.amountCents,
        monthly_spend_limit_cents: wallet?.monthlySpendLimitCents || 0,
      });
      setAr({
        enabled: res.data.autoRecharge.enabled,
        thresholdCents: res.data.autoRecharge.thresholdCents,
        amountCents: res.data.autoRecharge.amountCents,
      });
      toast.success(next.enabled ? 'Auto-recharge on' : 'Auto-recharge off');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save auto-recharge');
    } finally {
      setSavingAr(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="topup-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl my-6">
        <div className="font-extrabold text-slate-900 mb-1">Add marketing credits</div>
        <p className="text-xs text-slate-500 mb-4">Credits pay for campaign messages. Billed in {currency.toUpperCase()}.</p>
        <div className="flex gap-2 mb-3">
          {presets.map((a) => (
            <button key={a} onClick={() => setAmount(a)} data-testid={`topup-amount-${a}`}
              className={`flex-1 py-3 rounded-xl border font-extrabold text-sm ${amount === a ? 'border-[#2563EB] bg-[#EBF2FF] text-[#2563EB]' : 'border-slate-200 text-slate-500'}`}>
              {fmt(a)}
            </button>
          ))}
        </div>
        <div className="rounded-xl bg-slate-50 p-3 mb-4 space-y-1" data-testid="topup-fee-disclosure">
          {[
            ['Credits', fmt(amount)],
            [`BookingFlows fee (${Math.round(fees.platformPct * 100)}%)`, fmt(bfFee)],
            [`Card processing (${(fees.stripePct * 100).toFixed(1)}% + ${fmt(fees.stripeFixedCents)})`, fmt(stripeFee)],
          ].map(([label, val]) => (
            <div key={label} className="flex items-center justify-between text-[11px] text-slate-500">
              <span>{label}</span><span className="font-semibold text-slate-600">{val}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-xs font-extrabold text-slate-800 pt-1.5 mt-1.5 border-t border-slate-200/70">
            <span>Total charge</span><span data-testid="topup-total">{fmt(total)}</span>
          </div>
        </div>
        {saved ? (
          <>
            <button onClick={goRecharge} disabled={busy} data-testid="topup-recharge-btn"
              className="w-full py-3 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] disabled:opacity-60 inline-flex items-center justify-center gap-2 mb-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
              Charge {saved.brand ? saved.brand.charAt(0).toUpperCase() + saved.brand.slice(1) : 'card'} ····{saved.last4} — {fmt(total)}
            </button>
            <div className="flex items-center justify-between">
              <button onClick={onClose} className="text-xs font-bold text-slate-400 hover:text-slate-600" data-testid="topup-cancel-btn">Cancel</button>
              <button onClick={goCheckout} disabled={busy} data-testid="topup-checkout-btn"
                className="text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8]">Use a different card</button>
            </div>
          </>
        ) : (
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-slate-200 text-sm font-bold text-slate-500" data-testid="topup-cancel-btn">Cancel</button>
            <button onClick={goCheckout} disabled={busy} data-testid="topup-checkout-btn"
              className="flex-1 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Checkout
            </button>
          </div>
        )}

        {/* Auto-recharge */}
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="auto-recharge-section">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5 text-[#2563EB]" /> Auto-recharge
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {saved ? 'Never let a campaign stall on empty credits.' : 'Your card is saved securely at your first checkout — then you can turn this on.'}
              </p>
            </div>
            {savingAr
              ? <Loader2 className="w-4 h-4 animate-spin text-slate-300 shrink-0" />
              : <Toggle on={ar.enabled} disabled={!saved} testid="auto-recharge-toggle"
                  onChange={(v) => saveAutoRecharge({ ...ar, enabled: v })} />}
          </div>
          {ar.enabled && saved && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">When balance falls below</label>
                <select value={ar.thresholdCents} data-testid="auto-recharge-threshold"
                  onChange={(e) => saveAutoRecharge({ ...ar, thresholdCents: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold">
                  {thresholds.map((t) => <option key={t} value={t}>{fmt(t)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Recharge with</label>
                <select value={ar.amountCents} data-testid="auto-recharge-amount"
                  onChange={(e) => saveAutoRecharge({ ...ar, amountCents: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold">
                  {presets.map((a) => <option key={a} value={a}>{fmt(a)}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
        {saved && <p className="text-[10px] text-slate-400 mt-3">Card saved securely with Stripe at your first checkout. Auto-recharges include the same fees. Safety: max 2 auto-recharges per day, and your monthly spend limit (see Billing) always wins.</p>}
      </div>
    </div>
  );
};

const LIMIT_PRESETS = { usd: [0, 1000, 2500, 5000, 10000, 25000], aed: [0, 5000, 10000, 20000, 50000, 100000] };

const BillingModal = ({ open, onClose, orgId, wallet, onDone }) => {
  const { formatMoney } = useOperatorLocale();
  const [data, setData] = useState(null);
  const [savingLimit, setSavingLimit] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    apiV2.get(`/organizations/${orgId}/marketing/wallet/billing`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Could not load billing'));
  }, [open, orgId]);

  if (!open) return null;
  const fmt = (c) => formatMoney((c || 0) / 100);
  const currency = data?.currency || wallet?.currency || 'usd';
  const limits = LIMIT_PRESETS[currency] || LIMIT_PRESETS.usd;
  const limit = data?.monthlySpendLimitCents || 0;
  const mtd = data?.monthToDate;
  const pct = limit > 0 && mtd ? Math.min(100, Math.round((mtd.spentCents / limit) * 100)) : null;

  const saveLimit = async (val) => {
    setSavingLimit(true);
    try {
      const res = await apiV2.put(`/organizations/${orgId}/marketing/wallet/settings`, {
        auto_recharge_enabled: wallet?.autoRecharge?.enabled || false,
        threshold_cents: wallet?.autoRecharge?.thresholdCents || 500,
        amount_cents: wallet?.autoRecharge?.amountCents || 2500,
        monthly_spend_limit_cents: val,
      });
      setData((d) => ({ ...d, monthlySpendLimitCents: res.data.monthlySpendLimitCents }));
      toast.success(val ? `Monthly spend limit set to ${fmt(val)}` : 'Monthly spend limit removed');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save limit');
    } finally {
      setSavingLimit(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="billing-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg my-6 shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <div>
            <div className="font-extrabold text-slate-900">Billing & spend</div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">This month · billed in {currency.toUpperCase()}</div>
          </div>
          <button onClick={onClose} data-testid="billing-close-btn" className="p-2 rounded-full hover:bg-slate-100 text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
          {!data ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { label: 'Spent', value: fmt(mtd.spentCents), testid: 'billing-mtd-spent' },
                  { label: 'Credits added', value: fmt(mtd.toppedUpCents), testid: 'billing-mtd-topped' },
                  { label: 'Auto-recharges', value: mtd.autoRecharges, testid: 'billing-mtd-recharges' },
                ].map((m) => (
                  <div key={m.label} className="rounded-xl border border-slate-100 p-3" data-testid={m.testid}>
                    <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-1">{m.label}</div>
                    <MonoNum className="text-base font-extrabold text-slate-900">{m.value}</MonoNum>
                  </div>
                ))}
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 mb-5" data-testid="billing-limit-section">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-[#2563EB]" /> Monthly spend limit
                  </div>
                  {savingLimit && <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
                </div>
                <p className="text-[11px] text-slate-500 mb-3">
                  Hard stop for the whole account — campaigns pause and auto-recharge refuses once this month's spend hits the limit. No runaway cards, ever. (Auto-recharge is also hard-capped at {data.autoRechargeMaxPerDay}/day.)
                </p>
                <select value={limit} disabled={savingLimit} data-testid="billing-limit-select"
                  onChange={(e) => saveLimit(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold bg-white">
                  {limits.map((l) => <option key={l} value={l}>{l === 0 ? 'No limit' : `${fmt(l)} / month`}</option>)}
                </select>
                {pct != null && (
                  <div className="mt-3">
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : 'bg-[#2563EB]'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] font-bold text-slate-400 mt-1" data-testid="billing-limit-progress">{fmt(mtd.spentCents)} of {fmt(limit)} used ({pct}%)</div>
                  </div>
                )}
              </div>

              {data.byCampaign.length > 0 && (
                <div className="mb-5" data-testid="billing-by-campaign">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Spend by campaign — this month</div>
                  <div className="space-y-1.5">
                    {data.byCampaign.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-slate-50 last:border-0">
                        <span className="min-w-0 truncate font-semibold text-slate-700">{c.name} <span className="text-[10px] text-slate-400 font-bold uppercase">· {c.status}</span></span>
                        <MonoNum className="font-extrabold text-slate-900 whitespace-nowrap">{fmt(c.spendCents)} <span className="text-[10px] text-slate-400 font-semibold">/ {c.sent} sent</span></MonoNum>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div data-testid="billing-transactions">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Recent activity</div>
                {data.transactions.length === 0 ? (
                  <p className="text-sm text-slate-400">No activity yet.</p>
                ) : (
                  <div className="space-y-1">
                    {data.transactions.map((t) => (
                      <div key={t.id} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-slate-50 last:border-0">
                        <div className="min-w-0">
                          <span className="font-semibold text-slate-600 block truncate">{t.note || (t.type === 'burn' ? 'Campaign sends' : 'Credits top-up')}</span>
                          <span className="text-[10px] text-slate-400">{t.createdAt ? new Date(t.createdAt).toLocaleString() : ''}</span>
                        </div>
                        <MonoNum className={`font-extrabold whitespace-nowrap ${t.type === 'burn' ? 'text-slate-500' : 'text-emerald-600'}`}>
                          {t.type === 'burn' ? '−' : '+'}{fmt(t.amountCents)}
                        </MonoNum>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CampaignCard = ({ c, onAction, busy, formatMoney, delay, expanded, onToggle, orgId, allowedChannels = ['email'] }) => (
  <div className={`${CARD} p-5 intel-rise`} style={{ animationDelay: `${delay}ms` }} data-testid={`campaign-card-${c.id}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-slate-900 truncate">{c.name}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${STATUS_STYLE[c.status] || STATUS_STYLE.draft}`} data-testid={`campaign-status-${c.id}`}>{c.status}</span>
        </div>
        <div className="flex items-center gap-2 mt-1.5 text-slate-400">
          {(c.channels || []).map((ch) => {
            const Icon = CH_ICON[ch];
            return Icon ? <Icon key={ch} className="w-3.5 h-3.5" /> : null;
          })}
          <span className="text-[11px] font-semibold">{formatMoney(c.dailyBudgetCents / 100)}/day · every {c.frequencyDays}d max{c.variantCount > 1 ? ` · ${c.variantCount} ad variations` : ''}{c.discountCode ? ` · code ${c.discountCode}` : ''}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {c.status === 'active' && (
          <>
            <button onClick={() => onAction('run-now', c)} disabled={busy === c.id}
              title="Skip the wait — send today's batch right now (normally drips automatically once a day within your budget)"
              data-testid={`campaign-run-now-${c.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#EBF2FF] text-[#2563EB] text-xs font-bold hover:bg-blue-100 disabled:opacity-50">
              {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Send now
            </button>
            <button onClick={() => onAction('pause', c)} title="Pause the daily drip" data-testid={`campaign-pause-${c.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-100 text-slate-500 text-xs font-bold hover:bg-slate-200">
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
          </>
        )}
        {(c.status === 'draft' || c.status === 'paused') && (
          <>
            <button onClick={() => onAction('launch', c)} title={c.status === 'draft' ? 'Launch the daily drip' : 'Resume the daily drip'} data-testid={`campaign-launch-${c.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#1D4ED8]">
              <Play className="w-3.5 h-3.5" /> {c.status === 'draft' ? 'Launch' : 'Resume'}
            </button>
            <button onClick={() => onAction('delete', c)} title="Delete" data-testid={`campaign-delete-${c.id}`}
              className="p-2 rounded-full bg-slate-100 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
    </div>
    {c.status === 'active' && (
      <p className="text-[11px] text-slate-400 mt-2" data-testid={`campaign-drip-note-${c.id}`}>
        Dripping automatically — a batch goes out each day within your {formatMoney(c.dailyBudgetCents / 100)}/day budget, during local business hours.{c.variantCount > 1 ? ' Ad variations rotate so no contact sees the same creative twice in a row.' : ''}
      </p>
    )}
    <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-50">
      {[
        { label: 'Sent', value: (c.delivered || 0).toLocaleString() },
        { label: 'Views', value: (c.views || 0).toLocaleString() },
        { label: 'Clicks', value: (c.clicks || 0).toLocaleString() },
        { label: 'CTR', value: c.delivered > 0 ? `${Math.round((100 * (c.clicks || 0)) / c.delivered)}%` : '—' },
      ].map((m) => (
        <div key={m.label}>
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-0.5">{m.label}</div>
          <MonoNum className={`text-base font-extrabold ${m.hl ? 'text-emerald-600' : 'text-slate-800'}`}>{m.value}</MonoNum>
        </div>
      ))}
    </div>
    {expanded && <CampaignAnalytics campaignId={c.id} orgId={orgId} formatMoney={formatMoney} channels={(c.channels || ['email']).filter((ch) => allowedChannels.includes(ch)).length ? (c.channels || ['email']).filter((ch) => allowedChannels.includes(ch)) : ['email']} />}
    <button onClick={() => onToggle(c.id)} data-testid={`campaign-analytics-toggle-${c.id}`}
      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8]">
      <BarChart3 className="w-3.5 h-3.5" />
      {expanded ? 'Hide analytics' : 'Open analytics'}
      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
    </button>
  </div>
);

export default function CampaignsReport() {
  const orgId = localStorage.getItem('organizationId');
  const { formatMoney } = useOperatorLocale();
  const isAdminUser = getIntelAuth().isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitial, setWizardInitial] = useState(null);

  // Guided tour opens/closes the composer via this event
  useEffect(() => {
    const h = (e) => { setWizardInitial(null); setWizardOpen(!!e.detail?.open); };
    window.addEventListener('intel-tour-campaign-wizard', h);
    return () => window.removeEventListener('intel-tour-campaign-wizard', h);
  }, []);
  const [topupOpen, setTopupOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fmt = (cents) => formatMoney((cents || 0) / 100);

  const load = useCallback(async () => {
    try {
      const res = await apiV2.get(`/organizations/${orgId}/marketing/overview`);
      setData(res.data);
      setExpandedId((prev) => prev || res.data.campaigns?.[0]?.id || null);
    } catch {
      toast.error('Could not load campaigns');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const topup = searchParams.get('topup');
    const sessionId = searchParams.get('session_id');
    if (topup === 'success' && sessionId) {
      apiV2.post(`/organizations/${orgId}/marketing/wallet/confirm`, { session_id: sessionId })
        .then((res) => {
          if (res.data.credited) toast.success(`Credits added — balance ${fmt(res.data.balanceCents)}`);
          load();
        })
        .catch(() => toast.error('Could not confirm payment — contact support if you were charged'));
      setSearchParams({}, { replace: true });
    } else if (topup === 'cancelled') {
      setSearchParams({}, { replace: true });
    } else if (searchParams.get('create') === '1') {
      setWizardInitial({ goal: searchParams.get('goal') || undefined, segment: searchParams.get('segment') || undefined });
      setWizardOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line

  const onAction = async (action, c) => {
    if (action === 'delete' && !window.confirm(`Delete "${c.name}"?`)) return;
    setBusy(c.id);
    try {
      if (action === 'run-now') {
        const res = await apiV2.post(`/organizations/${orgId}/marketing/campaigns/${c.id}/run-now`);
        const r = res.data;
        if (r.skipped) toast.info(`Skipped: ${String(r.skipped).replaceAll('_', ' ')}`);
        else toast.success(`Cycle done — ${r.sent} sent, ${fmt(r.creditsBurnedCents || 0)} used`);
      } else if (action === 'delete') {
        await apiV2.delete(`/organizations/${orgId}/marketing/campaigns/${c.id}`);
        toast.success('Campaign deleted');
      } else {
        await apiV2.post(`/organizations/${orgId}/marketing/campaigns/${c.id}/${action}`);
        toast.success(action === 'pause' ? 'Campaign paused' : 'Campaign live');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const w = data?.wallet;
  const campaigns = data?.campaigns || [];
  const totals = campaigns.reduce((a, c) => ({
    sent: a.sent + (c.delivered || 0),
    views: a.views + (c.views || 0),
    clicks: a.clicks + (c.clicks || 0),
    bookings: a.bookings + (c.attributedBookings || 0),
    revenue: a.revenue + (c.attributedRevenue || 0),
  }), { sent: 0, views: 0, clicks: 0, bookings: 0, revenue: 0 });

  return (
    <IntelligenceShell>
      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="campaigns-report">
        <ReportHeader
          title="Campaigns"
          blurb="Re-engage the audience you've earned — AI-drafted email, SMS and WhatsApp drips with full booking attribution. You only pay per message sent."
          right={
            <>
              <AiSuggestion report="campaigns" months={12} />
              <button onClick={() => { setWizardInitial(null); setWizardOpen(true); }} data-testid="new-campaign-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] transition-colors">
                <Plus className="w-4 h-4" /> New campaign
              </button>
            </>
          }
        />
        {loading || !data ? <ReportSkeleton /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
              <div className={`${CARD} p-4 sm:p-5 intel-rise`} data-testid="camp-kpi-balance">
                <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Credit balance</div>
                <div className="flex items-end justify-between gap-2 flex-wrap">
                  <MonoNum className="text-lg sm:text-2xl font-extrabold tracking-tight text-[#2563EB]">{fmt(w.balanceCents)}</MonoNum>
                  {isAdminUser && (
                    <button onClick={() => setTopupOpen(true)} data-testid="add-credits-btn"
                      className="text-[11px] font-bold text-[#2563EB] hover:text-[#1D4ED8] inline-flex items-center gap-1">
                      <Wallet className="w-3.5 h-3.5" /> Add
                    </button>
                  )}
                </div>
                {isAdminUser && (
                <div className="mt-1.5 flex items-center gap-2.5 flex-wrap">
                  <button onClick={() => setTopupOpen(true)} data-testid="auto-recharge-status"
                    className="text-[10px] font-semibold text-slate-400 hover:text-[#2563EB] inline-flex items-center gap-1">
                    <RefreshCw className="w-2.5 h-2.5" />
                    {w.autoRecharge?.enabled ? `Auto-recharge on · below ${fmt(w.autoRecharge.thresholdCents)}` : 'Auto-recharge off'}
                  </button>
                  <button onClick={() => setBillingOpen(true)} data-testid="open-billing-btn"
                    className="text-[10px] font-semibold text-slate-400 hover:text-[#2563EB] inline-flex items-center gap-1">
                    <Receipt className="w-2.5 h-2.5" />
                    Billing{w.monthlySpendLimitCents ? ` · ${fmt(w.monthToDateSpendCents)}/${fmt(w.monthlySpendLimitCents)} this mo` : ''}
                  </button>
                </div>
                )}
              </div>
              <KpiCard label="Sent" value={totals.sent.toLocaleString()} testid="camp-kpi-sent" delay={40} />
              <KpiCard label="Views" value={totals.views.toLocaleString()} sub="opens across all campaigns" testid="camp-kpi-views" delay={70} />
              <KpiCard label="Clicks" value={totals.clicks.toLocaleString()} testid="camp-kpi-clicks" delay={100} />
              <KpiCard label="Click rate" value={totals.sent > 0 ? `${Math.round((100 * totals.clicks) / totals.sent)}%` : '—'} sub="the number to grow — clicks turn into bookings" testid="camp-kpi-ctr" delay={130} />
            </div>

            {campaigns.length === 0 ? (
              <div className={`${CARD} p-10 text-center intel-rise`} data-testid="campaigns-empty-state">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center mb-4">
                  <Send className="w-6 h-6" strokeWidth={1.75} />
                </div>
                <div className="font-extrabold text-slate-900 mb-1.5">Your first campaign is 2 minutes away</div>
                <p className="text-sm text-slate-500 max-w-md mx-auto mb-5">Pick a goal, choose an audience segment, set a few dollars a day — AI drafts the message and we handle sending, opt-outs and attribution.</p>
                <button onClick={() => { setWizardInitial(null); setWizardOpen(true); }} data-testid="empty-new-campaign-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8]">
                  <Plus className="w-4 h-4" /> Create campaign
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {campaigns.map((c, i) => (
                  <CampaignCard key={c.id} c={c} onAction={onAction} busy={busy} formatMoney={formatMoney} delay={i * 60}
                    orgId={orgId} allowedChannels={data?.allowedChannels || ['email']} expanded={expandedId === c.id} onToggle={(id) => setExpandedId(expandedId === id ? null : id)} />
                ))}
              </div>
            )}

            <p className="text-[11px] text-slate-400 mt-6" data-testid="camp-compliance-note">
              Campaigns only message contacts who opted in to marketing. Every message carries an unsubscribe link or STOP keyword, capped at one marketing message per contact per {campaigns[0]?.frequencyDays || 7} days across all campaigns. Marketing sends never affect booking confirmations or trip updates.
            </p>
          </>
        )}
      </div>
      <CampaignWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={load} overview={data} initial={wizardInitial} />
      <TopupModal open={topupOpen} onClose={() => setTopupOpen(false)} orgId={orgId} wallet={w} onDone={load} />
      <BillingModal open={billingOpen} onClose={() => setBillingOpen(false)} orgId={orgId} wallet={w} onDone={load} />
    </IntelligenceShell>
  );
}
